// hooks/useNotesData.js
import { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

export const useNotesData = (
  userId,
  apiUrl,
  generateFilterCombinations,
  activeSearchTerm,
  groupFilteredNotesByJob,
  setFilteredStackedJobs
) => {
  const [filteredNotesFromApi, setFilteredNotesFromApi] = useState([]);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const fetchNotesTimeoutRef = useRef(null);
 

  const fetchFilteredNotesForCombination = useCallback(async (combination, searchText) => {
    if (!userId) return [];
    try {
      const params = new URLSearchParams({ pageNumber: "1", pageSize: "100", userId });
      if (combination.date) params.append("date", combination.date);
      if (combination.workspace) params.append("workspaceId", combination.workspace);
      if (combination.project) params.append("projectId", combination.project);
      if (combination.job) params.append("jobId", combination.job);
      if (combination.userName) params.append("siteNoteUserId", combination.userName);
      if (combination.task) params.append("taskId", combination.task);
      
      let url;
      if (searchText?.trim()) {
        params.append("searchText", searchText);
        url = `${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`;
      } else {
        url = `${apiUrl}/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
      }
      const response = await fetch(url);
      if (!response.ok) return [];
      const data = await response.json();
      const notesList = data.data || data.siteNotes || [];

      return notesList.map(n => {
        return {
          ...n,
          id: n.id,
          userName: n.userName || n.UserName,
          documentCount: n.documentCount ?? n.DocumentCount ?? 0,
          note: n.note || n.content || "",
          timeStamp: n.timeStamp || n.date,
          date: n.date || n.createdDate || new Date().toISOString(),
          workspace: n.workspace || n.workspaceName || "",
          project: n.project || n.projectName || "",
          job: n.job || "",
          jobId: n.jobId || n.job || "",
          friendlyId: n.friendlyId || n.jobFriendlyId || n.jobId || "",
          jobFriendlyId: n.jobFriendlyId ||  "",
        };
      });
    } catch (error) {
      console.error("Error fetching notes for combination:", error);
      return [];
    }
  }, [apiUrl, userId]);

  const fetchNotesWithFilters = useCallback(async (filters) => {
    if (!userId) return;
    setLoadingFiltered(true);
    setFilteredStackedJobs([]);
    try {
      const combos = generateFilterCombinations(filters);
      if (combos.length === 0) {
        setFilteredNotesFromApi([]);
        setFilteredStackedJobs([]);
        return;
      }
      const allNotes = [];
      const seen = new Set();
      const results = await Promise.all(
        combos.map(combo => fetchFilteredNotesForCombination(combo, activeSearchTerm))
      );
      results.forEach(notesArray => {
        notesArray.forEach(note => {
          if (!seen.has(note.id)) {
            seen.add(note.id);
            allNotes.push(note);
          }
        });
      });
      setFilteredNotesFromApi(allNotes);
      const grouped = groupFilteredNotesByJob(allNotes);
      setFilteredStackedJobs(grouped);
    } catch (error) {
      console.error("Error fetching filtered notes:", error);
      toast.error("Failed to fetch filtered notes");
      setFilteredNotesFromApi([]);
      setFilteredStackedJobs([]);
    } finally {
      setLoadingFiltered(false);
    }
  }, [userId, generateFilterCombinations, fetchFilteredNotesForCombination, activeSearchTerm, groupFilteredNotesByJob, setFilteredStackedJobs]);

  const debouncedFetchNotesWithFilters = useCallback((filters) => {
    if (fetchNotesTimeoutRef.current) clearTimeout(fetchNotesTimeoutRef.current);
    fetchNotesTimeoutRef.current = setTimeout(() => fetchNotesWithFilters(filters), 300);
  }, [fetchNotesWithFilters]);

  return {
    filteredNotesFromApi,
    loadingFiltered,
    setFilteredNotesFromApi,
    fetchNotesWithFilters,
    debouncedFetchNotesWithFilters,
  };
};